import {t, Trans} from '@lingui/macro';
import {ellipsis} from 'polished';
import React, {FC, useEffect, useState} from 'react';
import styled from 'styled-components';

import {useInitialEffect} from '../../../../../libs/shared/helpers/src/react/hooks/useEffectHelpers';
import {HandlerOf, intersperseNodes} from '../../../../../libs/shared/helpers/src/react/reactHelpers';
import {AsyncStatusesEnum} from '../../../../../libs/shared/helpers/src/types/asyncStatuses';
import {TextBox} from '../../../../../libs/shared/ui/src/components/boxedInputs/textBox';
import {Button} from '../../../../../libs/shared/ui/src/components/buttons/button';
import {ButtonVariantsEnum} from '../../../../../libs/shared/ui/src/components/buttons/buttonConstants';
import {ButtonGroup} from '../../../../../libs/shared/ui/src/components/buttons/buttonGroup/buttonGroup';
import {ButtonGroupSpacer} from '../../../../../libs/shared/ui/src/components/buttons/buttonGroup/buttonGroupSpacer';
import {Icon} from '../../../../../libs/shared/ui/src/components/icon/icon';
import {fontSizes, fontWeights} from '../../../../../libs/shared/ui/src/styles/fontStyles';
import {
  DateTimeFormatsEnum,
  DateTimeFormatter,
} from '../../../../../srcCommon/formatters/dateTime/dateTimeFormatter';
import {useLocalize} from '../../../../../srcCommon/internationalization/internationalizationContext';
import {AsyncLoader} from '../../../../components/common/loaders/asyncLoader';
import {useLinkNavigation} from '../../../../components/common/navigation/useLinkNavigation';
import {Skeleton} from '../../../../components/common/skeleton';
import {ApiTokenScopeModel} from '../../../../helpers/apiTokenHelpers';
import {useClipboard} from '../../../../helpers/hooks/clipboardHookHelpers';
import {buildTeammateDisplayName} from '../../../../helpers/modelHelpers';
import {useAppDispatch} from '../../../../helpers/reduxHelpers';
import {ApiTokenModel} from '../../../../models/api/apiTokenModel';
import {TeammateModel} from '../../../../models/api/teammateModel';
import {buildSettingsDevelopersPath} from '../../../../models/url/settingsUrlHelpers';
import {SettingsDevelopersPagesEnum, SettingsRootPagesEnum} from '../../../../models/url/settingsUrlModel';
import {updateApiTokenAsyncAction} from '../../../../store/data/apiTokens/apiTokensActions';
import {SettingsPanelId} from '../../../../store/panels/panelStateBase';
import {fetchApiTokensForSettingsAsyncAction} from '../../../../store/panels/settings/settingsPanelActions';
import {SettingsBannersEnum, useSettingsBanners} from '../../../banners/settingsBannersContext';

/*
 * Constants.
 */

const hiddenJwtDisplay = '**********************************************';

/*
 * Props.
 */

interface SettingsDevelopersApiTokenPageProps {
  panelId: SettingsPanelId;
  token?: ApiTokenModel;
  tokensAsyncStatus: AsyncStatusesEnum;
  scopes: ReadonlyArray<ApiTokenScopeModel>;
  teammate?: TeammateModel;
}

/*
 * Style.
 */

const StyledErrorWrapperDiv = styled.div`
  flex: 1;
  padding: 64px;
`;

const StyledContentWrapperDiv = styled.div`
  flex: 1;
  display: flex;
  flex-flow: column;
  max-width: 600px;
  gap: 16px;
  padding-bottom: 62px;
`;

const StyledTextBox = styled(TextBox)`
  max-width: 380px;
`;

const StyledJwtWrapperDiv = styled.div`
  display: flex;
  flex-flow: row;
  gap: 8px;
`;

const StyledJwtTextBox = styled(TextBox)`
  flex: 0 0 395px;
  ${ellipsis()};
  padding-right: 4px;
`;

const StyledButton = styled(Button)`
  height: 30px;
  margin-top: 23px;
`;

const StyledButtonContentWrapperDiv = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`;

const StyledIcon = styled(Icon)`
  color: ${(p) => p.theme.greys.shade70};
`;

const StyledTokenDetailsWrapperDiv = styled.div`
  display: flex;
  flex-flow: column;
  gap: 8px;
  margin-bottom: 16px;
`;

const StyledTokenDetailsTitleDiv = styled.div`
  font-size: ${fontSizes.small};
  color: ${(p) => p.theme.greys.shade70};
  font-weight: ${fontWeights.semibold};
`;

const StyledScopesWrapperDiv = styled.div`
  max-width: 300px;
  line-height: 20px;
`;

const StyledFooterDiv = styled.div`
  border-top: 1px solid ${(p) => p.theme.alphas.gray10};
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  padding: 16px 8px;
  background: ${(p) => p.theme.greys.white};
`;

const StyledEmptyStateWrapperDiv = styled.div`
  display: flex;
  flex-flow: column;
  padding: 64px;
  gap: 32px;
  text-align: center;
  flex: 1;
`;

const StyledButtonWrapperDiv = styled.div`
  display: flex;
  align-self: center;
`;

/*
 * Component.
 */

export const SettingsDevelopersApiTokenPage: FC<SettingsDevelopersApiTokenPageProps> = (props) => {
  const {panelId, token, tokensAsyncStatus, scopes, teammate} = props;
  const dispatch = useAppDispatch();
  const {copy} = useClipboard();
  const {showBanner} = useSettingsBanners();
  const localize = useLocalize();
  const {navigateToLink} = useLinkNavigation();

  const [tokenName, setTokenName] = useState(token?.name ?? '');
  const [shouldRevealJwt, setShouldRevealJwt] = useState(false);
  const isNameChanged = tokenName.trim() !== token?.name ?? '';
  const isNameInvalid = tokenName.trim() === '';

  useInitialEffect(() => {
    // If we are already loaded, we do not need to fetch anything.
    if (tokensAsyncStatus === AsyncStatusesEnum.LOADED) {
      return;
    }
    dispatch(fetchApiTokensForSettingsAsyncAction(panelId));
  });

  useEffect(() => {
    setTokenName(token?.name || '');
  }, [token?.name]);

  // If we failed to load tokens, display an ability to retry loading.
  if (tokensAsyncStatus === AsyncStatusesEnum.FAILED) {
    return (
      <StyledErrorWrapperDiv>
        <AsyncLoader
          status={tokensAsyncStatus}
          onRetryClick={() => dispatch(fetchApiTokensForSettingsAsyncAction(panelId))}
        />
      </StyledErrorWrapperDiv>
    );
  }

  if (tokensAsyncStatus === AsyncStatusesEnum.LOADING) {
    return (
      <StyledContentWrapperDiv>
        <Skeleton height="150px" />
      </StyledContentWrapperDiv>
    );
  }

  // Empty state for if we fail to find an application.
  if (!token) {
    return renderEmptyState(navigateToLink);
  }

  const onCopyClick = () => {
    copy(token.jwt);
    showBanner({
      type: SettingsBannersEnum.SUCCESS,
      message: <Trans id="settings-developers-token-api-token-copied">Copied JWT to clipboard.</Trans>,
    });
  };

  const onSaveClickAsync = async () => {
    try {
      await dispatch(updateApiTokenAsyncAction(token.id, {name: tokenName}));
    } catch {
      showBanner({
        type: SettingsBannersEnum.ERROR,
        message: (
          <Trans id="settings-developers-token-api-update-error">
            Error updating token. Please try again.
          </Trans>
        ),
      });
    }
  };

  return (
    <StyledContentWrapperDiv>
      <StyledTextBox
        label={<Trans id="settings-developers-token-api-token-name">API token name</Trans>}
        value={tokenName}
        onChange={setTokenName}
        isRequired
        hasError={isNameInvalid}
        errorMessage={
          isNameInvalid && (
            <Trans id="settings-developers-token-api-name-required-error">This is a required field</Trans>
          )
        }
        placeholder={localize(t('settings-developers-token-api-name-placeholder')`Add API token name`)}
      />
      <StyledJwtWrapperDiv>
        <StyledJwtTextBox
          label={<Trans id="settings-developers-token-api-token">API token</Trans>}
          value={shouldRevealJwt ? token.jwt : hiddenJwtDisplay}
          isDisabled
        />
        <StyledButton onClick={onCopyClick}>
          <StyledButtonContentWrapperDiv>
            <StyledIcon name="copySmall" />
            <Trans id="settings-developers-token-api-token-copy">Copy</Trans>
          </StyledButtonContentWrapperDiv>
        </StyledButton>
        <StyledButton onClick={() => setShouldRevealJwt((v) => !v)}>
          <StyledButtonContentWrapperDiv>
            <StyledIcon name={shouldRevealJwt ? 'eyeCrossedSmall' : 'eyeSmall'} />
            {!shouldRevealJwt && <Trans id="settings-developers-token-api-token-reveal">Reveal</Trans>}
            {shouldRevealJwt && <Trans id="settings-developers-token-api-token-hide">Hide</Trans>}
          </StyledButtonContentWrapperDiv>
        </StyledButton>
      </StyledJwtWrapperDiv>
      {renderTokenDetails(<Trans id="settings-developers-token-api-token-id">ID</Trans>, token.publicId)}
      {renderTokenDetails(
        <Trans id="settings-developers-token-api-token-created-on">Created on</Trans>,
        <DateTimeFormatter value={token.createdAt} format={DateTimeFormatsEnum.ABSOLUTE} />,
      )}
      {renderTokenDetails(
        <Trans id="settings-developers-token-api-token-created-by">Created by</Trans>,
        <>
          {teammate ? (
            buildTeammateDisplayName(teammate)
          ) : (
            <Trans id="settings-developers-token-api-token-no-creator">---</Trans>
          )}
        </>,
      )}
      {renderTokenDetails(
        <Trans id="settings-developers-token-api-token-last-activity">Last activity on</Trans>,
        <DateTimeFormatter value={token.lastUsedAt} format={DateTimeFormatsEnum.ABSOLUTE} />,
      )}
      {renderTokenDetails(
        <Trans id="settings-developers-token-api-token-scopes">Scope</Trans>,
        <StyledScopesWrapperDiv>
          {intersperseNodes(
            token.scopes.map((scope) => ({
              node: scopes.find((s) => s.value === scope)?.display || <>{scope}</>,
              key: scope,
            })),
            () => ', ',
          )}
        </StyledScopesWrapperDiv>,
      )}
      {isNameChanged && (
        <StyledFooterDiv>
          <ButtonGroup>
            <ButtonGroupSpacer />
            <Button onClick={() => setTokenName(token.name ?? '')}>
              <Trans id="settings-developers-token-api-token-cancel-button">Cancel</Trans>
            </Button>
            <Button
              variant={ButtonVariantsEnum.PRIMARY}
              isDisabled={tokenName.trim() === ''}
              onClick={onSaveClickAsync}
            >
              <Trans id="settings-developers-token-api-token-save-button">Save</Trans>
            </Button>
          </ButtonGroup>
        </StyledFooterDiv>
      )}
    </StyledContentWrapperDiv>
  );
};

/*
 * Helpers.
 */

function renderTokenDetails(title: React.ReactNode, content: React.ReactNode) {
  return (
    <StyledTokenDetailsWrapperDiv>
      <StyledTokenDetailsTitleDiv>{title}</StyledTokenDetailsTitleDiv>
      {content}
    </StyledTokenDetailsWrapperDiv>
  );
}

function renderEmptyState(navigateToLink: HandlerOf<string>) {
  const onButtonClick = () => {
    navigateToLink(
      buildSettingsDevelopersPath({
        rootPage: SettingsRootPagesEnum.DEVELOPERS,
        subPage: SettingsDevelopersPagesEnum.TOKENS,
      }),
    );
  };

  return (
    <StyledEmptyStateWrapperDiv>
      <Trans id="settings-developers-token-empty-state">We are not able to find this token.</Trans>
      <StyledButtonWrapperDiv>
        <Button variant={ButtonVariantsEnum.PRIMARY} onClick={onButtonClick}>
          <Trans id="settings-developers-token-empty-state-button">Back</Trans>
        </Button>
      </StyledButtonWrapperDiv>
    </StyledEmptyStateWrapperDiv>
  );
}
